import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  subscribeSaveErrors,
  clearSaveErrors,
  isShowcaseMode,
  type SaveErrorEntry,
} from '../lib/saveStatus';

const SCOPE_LABELS: Record<string, string> = {
  config: 'saveError.scopeConfig',
  musicians: 'saveError.scopeMusicians',
  crew: 'saveError.scopeCrew',
  stageplots: 'saveError.scopeStageplots',
  bands: 'saveError.scopeBands',
  show: 'saveError.scopeShow',
  setlist: 'saveError.scopeSetlist',
  rider: 'saveError.scopeRider',
  team: 'saveError.scopeTeam',
  finance: 'saveError.scopeFinance',
  ai: 'saveError.scopeAi',
  checklists: 'saveError.scopeChecklists',
};

/**
 * Persistent banner shown whenever a save/load has failed.
 * Rendered once in App.tsx so it covers every page.
 */
export default function SaveErrorBanner() {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<SaveErrorEntry[]>([]);

  useEffect(() => subscribeSaveErrors(setErrors), []);

  // Showcase (demo) account: nothing is ever saved, so a "Not saved" warning
  // would be a lie. Never render it. (2026-09-15)
  if (isShowcaseMode()) return null;
  if (errors.length === 0) return null;

  return (
    <div className="save-error-banner" role="alert" aria-live="assertive">
      <div className="save-error-banner-body">
        <strong>{t('saveError.notSaved')}</strong>
        <span>
          {errors
            .map((e) => `${t(SCOPE_LABELS[e.scope] || e.scope)}: ${e.message}`)
            .join(' · ')}
        </span>
        <span className="save-error-banner-hint">
          {t('saveError.hint')}
        </span>
      </div>
      <button
        type="button"
        className="save-error-banner-close"
        onClick={clearSaveErrors}
        aria-label={t('saveError.dismiss')}
      >
        ×
      </button>
    </div>
  );
}
